/**
 * Manual block-order overrides for stream / collection surfaces.
 *
 * Stores per-scope ordered lists of block ids that the user has
 * arranged via the gesture-driven reorder affordance. Persistence is
 * **localStorage only** (this device, no cross-device sync), matching
 * the precedent set by `collection-view-mode-store.ts` and the v1
 * implementation of `homepage-pins.ts`.
 *
 * TODO(persistence): Graduate this to a server-side per-user-per-scope
 * ordering table so a user's arrangement follows them across devices
 * and survives a `localStorage` wipe. Likely shape: a `BlockOrder`
 * table keyed `(userId, scopeKey)` with a `JSONB` payload of the
 * ordered id list, fronted by an optimistic-update path on the
 * client that mirrors `homepage-pins.ts`'s API sync (bootstrap on
 * mount, save on each mutation, rollback on failure). The exported
 * store surface below is the intended API; only the load / save
 * internals would change. Until then the in-file storage caps
 * (`MAX_IDS_PER_SCOPE`, `MAX_SCOPES`) and LRU eviction are the
 * safety net against unbounded growth on heavy users.
 *
 * Scope keys are flat strings owned by the caller. Conventions:
 *
 *   "stream:home:2026-05-09"        — home stream, day 2026-05-09
 *   "stream:home:flat"              — home stream (if ever rendered flat)
 *   "collection:abc123:2026-05-09"  — collection abc123, chronological day
 *   "collection:abc123:flat"        — collection abc123, grid view
 *   "smart:transient42:flat"        — smart collection, transient id
 *
 * **Pinned section reorder DOES NOT use this store.** Pins are owned
 * by `state/homepage-pins.ts` and reorder writes through
 * `pinnedStreamStore.reorder(...)` so all pin state stays in one
 * place.
 *
 * Reconciliation: callers pass their CURRENT live list of block ids
 * and a stored order, and `reconcile` returns the display order:
 *   - drop ids that no longer exist
 *   - append unknown ids at the END (preserves the user's existing
 *     arrangement when new captures land in a manually-ordered day)
 *
 * Storage caps:
 *   - Per-scope: 10_000 ids (truncate from the front if exceeded —
 *     keeps the most-recent end of the list)
 *   - Total scopes: 200 (evict LRU when over budget)
 *
 * Each surviving scope tracks a `touchedAt` timestamp so LRU eviction
 * can target the least-recently-written entries.
 */
import { useSyncExternalStore } from "react";
const STORAGE_KEY = "viewer-manual-order-v1";
const MAX_IDS_PER_SCOPE = 10_000;
const MAX_SCOPES = 200;
const listeners = new Set();
function emit() {
    for (const l of listeners)
        l();
}
function isValidId(x) {
    return typeof x === "string" && x.length > 0 && x.length <= 256;
}
function parseStored(raw) {
    if (!raw)
        return {};
    try {
        const parsed = JSON.parse(raw);
        if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
            return {};
        }
        const out = {};
        for (const [key, value] of Object.entries(parsed)) {
            if (typeof key !== "string" || key.length === 0)
                continue;
            if (value === null || typeof value !== "object" || Array.isArray(value))
                continue;
            const v = value;
            const rawIds = v.ids;
            const touchedAt = typeof v.touchedAt === "number" ? v.touchedAt : 0;
            if (!Array.isArray(rawIds))
                continue;
            const ids = [];
            const seen = new Set();
            for (const id of rawIds) {
                if (!isValidId(id))
                    continue;
                if (seen.has(id))
                    continue;
                seen.add(id);
                ids.push(id);
                if (ids.length >= MAX_IDS_PER_SCOPE)
                    break;
            }
            if (ids.length === 0)
                continue;
            out[key] = { ids, touchedAt };
        }
        return out;
    }
    catch (e) {
        console.warn("Failed to parse manual-order store:", e);
        return {};
    }
}
function loadFromStorage() {
    if (typeof window === "undefined")
        return {};
    try {
        return parseStored(localStorage.getItem(STORAGE_KEY));
    }
    catch (e) {
        console.warn("Failed to read manual-order store:", e);
        return {};
    }
}
function saveToStorage(map) {
    if (typeof window === "undefined")
        return;
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    }
    catch (e) {
        console.warn("Failed to write manual-order store:", e);
    }
}
function evictLruIfOverBudget(map) {
    const entries = Object.entries(map);
    if (entries.length <= MAX_SCOPES)
        return map;
    entries.sort((a, b) => b[1].touchedAt - a[1].touchedAt);
    const keep = entries.slice(0, MAX_SCOPES);
    const next = {};
    for (const [k, v] of keep)
        next[k] = v;
    return next;
}
let snapshot = loadFromStorage();
// Mirror the homepage-pins pattern: another tab writing to the same
// storage key fires a `storage` event in this one, so the snapshot
// (and every `useOrder` consumer) refreshes without a manual poll.
if (typeof window !== "undefined") {
    window.addEventListener("storage", (e) => {
        if (e.key === STORAGE_KEY) {
            snapshot = loadFromStorage();
            emit();
        }
    });
}
function subscribe(listener) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
function getOrder(scopeKey) {
    const entry = snapshot[scopeKey];
    return entry?.ids;
}
function setOrder(scopeKey, ids) {
    if (typeof scopeKey !== "string" || scopeKey.length === 0)
        return;
    const seen = new Set();
    const cleaned = [];
    for (const id of ids) {
        if (!isValidId(id))
            continue;
        if (seen.has(id))
            continue;
        seen.add(id);
        cleaned.push(id);
        if (cleaned.length >= MAX_IDS_PER_SCOPE)
            break;
    }
    const next = { ...snapshot };
    if (cleaned.length === 0) {
        if (!(scopeKey in next))
            return;
        delete next[scopeKey];
    }
    else {
        next[scopeKey] = { ids: cleaned, touchedAt: Date.now() };
    }
    const evicted = evictLruIfOverBudget(next);
    snapshot = evicted;
    saveToStorage(evicted);
    emit();
}
/**
 * Per-scope `useOrder` selector. Returns `undefined` when the scope
 * has no stored override, so consumers can fall back to the server
 * order without an extra branch.
 *
 * Returns the same array reference between renders when the
 * underlying data hasn't changed, so `useMemo` consumers downstream
 * stay cache-stable.
 */
function useOrder(scopeKey) {
    return useSyncExternalStore(subscribe, () => snapshot[scopeKey]?.ids, () => undefined);
}
/**
 * Merge a stored manual order with the live id list. Drops any
 * stored id that's no longer present, then appends any live id the
 * stored order doesn't already cover at the END of the result.
 *
 * Stable identity: returns the `live` array reference unchanged when
 * no stored order exists, so consumers wrapping this in `useMemo`
 * don't re-render needlessly.
 */
function reconcile(stored, live) {
    if (!stored || stored.length === 0)
        return live;
    const liveSet = new Set(live);
    const ordered = [];
    const known = new Set();
    for (const id of stored) {
        if (!liveSet.has(id))
            continue;
        if (known.has(id))
            continue;
        known.add(id);
        ordered.push(id);
    }
    const tail = [];
    for (const id of live) {
        if (known.has(id))
            continue;
        tail.push(id);
    }
    if (tail.length === 0 && ordered.length === live.length) {
        return ordered;
    }
    return [...ordered, ...tail];
}
/**
 * Drop everything (e.g. sign-out). Doesn't fire a storage event for
 * other tabs because cross-tab signed-in state is already managed
 * elsewhere.
 */
function clearAll() {
    snapshot = {};
    if (typeof window !== "undefined") {
        try {
            localStorage.removeItem(STORAGE_KEY);
        }
        catch {
            /* ignore */
        }
    }
    emit();
}
export const manualOrderStore = {
    getOrder,
    setOrder,
    useOrder,
    reconcile,
    clearAll,
};
/**
 * Build a scope key. Substitutes the synthetic `__all__` group key
 * emitted by the Stream's `groupByNone` strategy with the readable
 * literal `flat`, so flat-view keys end up as e.g.
 * `collection:abc:flat` rather than `collection:abc:__all__`.
 */
export function buildManualOrderScopeKey(surfaceKey, groupKey) {
    const tail = groupKey === "__all__" ? "flat" : groupKey;
    return `${surfaceKey}:${tail}`;
}
